import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
  Collapse,
  Chip,
} from '@mui/material';
import { CaretDown } from '@phosphor-icons/react';
import { useState, useEffect } from 'react';
import theme from '../../../theme';

interface MenuSidebarProps {
  menuData: any;
  selectedCategory: string;
  selectedSubcategory: string;
  onCategorySelect: (category: string, subcategory?: string) => void;
  searchQuery?: string;
}

const MenuSidebar = ({
  menuData,
  selectedCategory,
  selectedSubcategory,
  onCategorySelect,
  searchQuery = '',
}: MenuSidebarProps) => {
  const [expandedCategories, setExpandedCategories] = useState<string[]>([]);

  // Auto-expand categories that have search matches
  useEffect(() => {
    if (searchQuery && menuData?.results) {
      const categoriesToExpand = menuData.results
        .filter(
          (category: any) =>
            category._hasMatchingSubcategories ||
            category.subcategories?.some((sub: any) => sub._hasMatchingItems)
        )
        .map((category: any) => category.title);

      setExpandedCategories(categoriesToExpand);
    } else if (!searchQuery) {
      // Collapse all when search is cleared
      setExpandedCategories([]);
    }
  }, [searchQuery, menuData]);

  const handleCategoryClick = (categoryTitle: string) => {
    const category = menuData?.results?.find(
      (cat: any) => cat.title === categoryTitle
    );

    if (!category) return;

    if (category?.subcategories?.length > 0) {
      // Toggle expansion for categories with subcategories
      setExpandedCategories(prev =>
        prev.includes(categoryTitle)
          ? prev.filter(cat => cat !== categoryTitle)
          : [...prev, categoryTitle]
      );

      // If not expanded, select the category and first subcategory
      if (!expandedCategories.includes(categoryTitle)) {
        // Find the first subcategory with items (or first available)
        const firstSubcategory =
          category.subcategories.find(
            (sub: any) => sub.items?.length > 0 || sub._hasMatchingItems
          ) || category.subcategories[0];

        if (firstSubcategory) {
          onCategorySelect(categoryTitle, firstSubcategory.title);
        } else {
          onCategorySelect(categoryTitle);
        }
      } else {
        // If already expanded, just select the category (will show main category items)
        onCategorySelect(categoryTitle);
      }
    } else {
      // For categories without subcategories, select directly
      onCategorySelect(categoryTitle);
    }
  };

  const handleSubcategoryClick = (
    categoryTitle: string,
    subcategoryTitle: string
  ) => {
    onCategorySelect(categoryTitle, subcategoryTitle);
  };

  // Function to get item count for display
  const getItemCount = (category: any, subcategory?: any) => {
    if (subcategory) {
      return subcategory.items?.length || 0;
    }
    return category.items?.length || 0;
  };

  return (
    <Box
      sx={{
        width: 240,
        borderRight: '1px solid',
        borderColor: 'divider',
        height: '100%',
      }}
    >
      <Typography
        variant="h6"
        sx={{
          padding: '10.5px 24px',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        Menu Categories
        {searchQuery && (
          <Chip
            label={`Search: "${searchQuery}"`}
            size="small"
            sx={{
              ml: 1,
              fontSize: '10px',
              height: '20px',
              backgroundColor: '#E7F0FF',
              color: '#1976d2',
            }}
          />
        )}
      </Typography>
      <Box
        sx={{
          overflowY: 'auto',
          flex: 1,
          '&::-webkit-scrollbar': { display: 'none' },
          msOverflowStyle: 'none',
          scrollbarWidth: 'none',
          maxHeight: 'calc(100vh - 200px)',
        }}
      >
        <List>
          {menuData?.results?.map((category: any) => (
            <Box key={category.title}>
              <ListItem disablePadding>
                <ListItemButton
                  selected={selectedCategory === category.title}
                  onClick={() => handleCategoryClick(category.title)}
                  sx={{
                    '&.Mui-selected': {
                      bgcolor: '#E7F0FF',
                      '&:hover': {
                        bgcolor: '#E7F0FF',
                      },
                    },
                    '&:hover': {
                      bgcolor: '#F5F5F5',
                    },
                  }}
                >
                  <ListItemText
                    primary={
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                      >
                        {category.title.toLowerCase().charAt(0).toUpperCase() +
                          category.title.toLowerCase().slice(1)}
                        {searchQuery && getItemCount(category) > 0 && (
                          <Chip
                            label={getItemCount(category)}
                            size="small"
                            sx={{
                              fontSize: '10px',
                              height: '16px',
                              backgroundColor: '#E7F0FF',
                              color: '#1976d2',
                            }}
                          />
                        )}
                      </Box>
                    }
                    sx={{
                      fontSize: '14px',
                      fontWeight: 500,
                    }}
                  />
                  {category.subcategories?.length > 0 && (
                    <CaretDown
                      size={16}
                      color="#6B6F80"
                      style={{
                        transform: expandedCategories.includes(category.title)
                          ? 'rotate(180deg)'
                          : 'none',
                        transition: 'transform 0.2s ease',
                      }}
                    />
                  )}
                </ListItemButton>
              </ListItem>

              {/* Subcategories */}
              {category.subcategories?.length > 0 && (
                <Collapse
                  in={expandedCategories.includes(category.title)}
                  timeout="auto"
                  unmountOnExit
                >
                  <List component="div" disablePadding>
                    {category.subcategories.map(
                      (subcategory: any, index: number) => (
                        <ListItem
                          key={subcategory.title}
                          component="div"
                          onClick={() =>
                            handleSubcategoryClick(
                              category.title,
                              subcategory.title
                            )
                          }
                          sx={{
                            pl: 4,
                            position: 'relative',
                            cursor: 'pointer',
                            '&::before': {
                              content: '""',
                              position: 'absolute',
                              left: '8px',
                              top: index === 0 ? '-25%' : '0',
                              bottom:
                                index === category.subcategories.length - 1
                                  ? '45%'
                                  : '0',
                              width: '1px',
                              backgroundColor: theme.palette.lightGray.main,
                            },
                            '&::after': {
                              content: '""',
                              position: 'absolute',
                              left: '8px',
                              top: '51%',
                              width: '12px',
                              height: '2px',
                              backgroundColor: theme.palette.lightGray.main,
                            },
                          }}
                        >
                          <Box
                            sx={{
                              width: '100%',
                              ml: '-18px',
                              pl: '10px',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                            }}
                          >
                            <ListItemText
                              primary={
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                  }}
                                >
                                  {subcategory.title
                                    .toLowerCase()
                                    .charAt(0)
                                    .toUpperCase() +
                                    subcategory.title.toLowerCase().slice(1)}
                                  {searchQuery &&
                                    getItemCount(category, subcategory) > 0 && (
                                      <Chip
                                        label={getItemCount(
                                          category,
                                          subcategory
                                        )}
                                        size="small"
                                        sx={{
                                          fontSize: '10px',
                                          height: '16px',
                                          backgroundColor: '#E7F0FF',
                                          color: '#1976d2',
                                        }}
                                      />
                                    )}
                                </Box>
                              }
                              sx={{
                                '& .MuiTypography-root': {
                                  borderRadius: '8px',
                                  fontSize: '14px',
                                  fontWeight: '400',
                                  color: theme.palette.lightGray.text,
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  backgroundColor:
                                    selectedCategory === category.title &&
                                    selectedSubcategory === subcategory.title
                                      ? '#E7F0FF'
                                      : 'transparent',
                                  px: 1,
                                  py: 0.5,
                                },
                              }}
                            />
                          </Box>
                        </ListItem>
                      )
                    )}
                  </List>
                </Collapse>
              )}
            </Box>
          ))}
        </List>
      </Box>
    </Box>
  );
};

export default MenuSidebar;
